import React from 'react';
import { aggregateState, receiverState, stateLabel, stateSummary } from '../utils/receiverStatus';
import './Map.css';

export const ALL_RECEIVERS = 'All Receivers';

/**
 * The map's receiver chip: an antenna icon in the selected feed's status colour, its name,
 * and a status dot. Shared by Live and History so both pages pick a receiver the same way.
 *
 * "All Receivers" reports the worst case that still matters — green only when every
 * receiver is feeding us — because a green chip while three of four are dead would hide
 * the outage it exists to report.
 */
export function ReceiverChip({ receivers, selected, open, onClick }) {
  const state = selected === ALL_RECEIVERS
    ? aggregateState(receivers)
    : receiverState((receivers || []).find(r => r.receiverName === selected));
  const statusText = selected === ALL_RECEIVERS
    ? (stateSummary(receivers) || 'no receivers')
    : stateLabel(state);
  const label = !selected ? 'Select receiver'
    : selected === ALL_RECEIVERS ? 'All receivers' : selected;

  return (
    <button
      className={`map-ctl-btn map-ctl-btn-wide ${open ? 'open' : ''}`}
      onClick={onClick}
      title={`Receiver: ${label} — ${statusText}`}
      aria-expanded={open}
    >
      {/* The icon carries the STATUS colour, so the name beside it stays plain and
          readable at a glance rather than changing colour under the reader. */}
      <svg className={`map-ctl-icon is-${state}`} width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 12v9" /><circle cx="12" cy="10" r="2.2" />
        <path d="M7.5 5.5a7 7 0 0 0 0 9" /><path d="M16.5 5.5a7 7 0 0 1 0 9" />
        <path d="M4.7 2.8a11 11 0 0 0 0 14.4" /><path d="M19.3 2.8a11 11 0 0 1 0 14.4" />
      </svg>
      <span className="map-ctl-label">{label}</span>
      <span className={`map-ctl-dot is-${state}`} aria-hidden="true" />
    </button>
  );
}

/** The chip's popup content: one radio row per receiver, optionally led by "All Receivers". */
export function ReceiverOptions({ receivers, selected, showAll, onSelect }) {
  return (
    <>
      <div className="map-filter-title">Receiver</div>
      {showAll && (
        <button
          className={`map-filter-option ${selected === ALL_RECEIVERS ? 'active' : ''}`}
          onClick={() => onSelect(ALL_RECEIVERS)}
        >
          <span className="map-filter-radio" aria-hidden="true" />
          All Receivers
        </button>
      )}
      {(receivers || []).map(r => (
        <button
          key={r.receiverUuid}
          className={`map-filter-option ${selected === r.receiverName ? 'active' : ''}`}
          onClick={() => onSelect(r.receiverName)}
        >
          <span className="map-filter-radio" aria-hidden="true" />
          <span className="map-filter-option-label">{r.receiverName}</span>
          <span
            className={`map-ctl-dot is-${receiverState(r)}`}
            title={stateLabel(receiverState(r))}
          />
        </button>
      ))}
    </>
  );
}
